// ImportColumnMappingTable — REAL implementation (CHG-1260).
//
// Replaces the CHG-1247 stub at apps/dashboard-v2/src/wizards/customRenders/
// ImportColumnMappingTable.tsx via the registerWizardComponent registration
// updated in ./registerWizardPrimitives.ts. The stub file itself is left
// orphaned but immutable per "CHG-1247 immutable" — a future cleanup CHG
// removes it.
//
// Used by import-map wizard step "map" (CHG-1241 manifest). Renders a per-
// row table mapping each canonical target column → a detected source column.
// On change, calls onChange with the column_mapping object (target → source).
// The wizard's onSubmitRequest in ImportMapWizard reads the value and POSTs
// to /imports/{id}/execute.
//
// Source columns + auto-detected initial mapping come from
// GET /imports/{import_batch_id}/mapping. Per the manifest's 2-phase API
// flow, the import_batch_id is available on `props.import_batch_id` (set by
// the wizard host once the upload step completes; until that wiring lands
// in a follow-up CHG, the component falls back to the entity-hint's
// canonical target list and renders empty source-column dropdowns).
//
// Target column list per entity_type follows v1 _HEADER_PATTERNS contract
// (CHG-0822). Subset shown here is the v2 ε set — future expansion lands
// when the T2 ImportBatch port arrives.

import { useCallback, useMemo } from "react";
import type { CustomRenderProps } from "../customRenderRegistry";

const TARGET_COLUMNS_BY_ENTITY: Record<string, string[]> = {
  donor: ["full_name", "email", "phone", "address_line1", "city", "country", "tags"],
  donation: ["donor_email", "amount", "currency", "date", "designation_name", "campaign_name", "source", "payment_method", "notes"],
  pledge: ["donor_email", "total_amount", "currency", "designation_name", "frequency", "start_date", "due_date"],
  recurring_donation: ["donor_email", "amount", "currency", "designation_name", "frequency", "start_date", "end_policy"],
  prospect: ["full_name", "email", "phone", "source", "notes"],
};

function targetColumnsFor(entityType: unknown): string[] {
  if (typeof entityType === "string" && entityType in TARGET_COLUMNS_BY_ENTITY) {
    return TARGET_COLUMNS_BY_ENTITY[entityType];
  }
  // Sensible default if entity_type isn't in the wizard values yet.
  return TARGET_COLUMNS_BY_ENTITY.donor;
}

export function ImportColumnMappingTable(props: CustomRenderProps) {
  const { value, onChange } = props;
  // The wizard host stores form values on the parent; entity_type lives at
  // values.entity_type but the FormDialog spread doesn't pass sibling field
  // values to a field component. We read it off props if set, else fall back.
  const entityType = props.entity_type ?? (props as Record<string, unknown>).__entity_type;
  const targets = useMemo(() => targetColumnsFor(entityType), [entityType]);
  // The source column list is forward-promised — once the wizard exposes
  // GET /imports/{id}/mapping data, the wizard host can pass `source_columns`
  // via field.props. For now the component renders an empty source list.
  const sourceColumns = useMemo<string[]>(() => {
    const fromProps = (props as Record<string, unknown>).source_columns;
    return Array.isArray(fromProps) ? (fromProps as string[]) : [];
  }, [props]);

  const mapping: Record<string, string | null> = useMemo(() => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, string | null>;
    }
    return {};
  }, [value]);

  const handleChange = useCallback(
    (target: string, source: string) => {
      const next = { ...mapping, [target]: source === "" ? null : source };
      onChange(next);
    },
    [mapping, onChange],
  );

  const mappedCount = targets.filter((t) => mapping[t]).length;

  return (
    <div data-wizard-component-id="ImportColumnMappingTable" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
        <strong>{mappedCount}</strong> of {targets.length} canonical fields mapped
        {sourceColumns.length === 0 ? (
          <span style={{ marginLeft: 8 }}>
            · upload a source file in step 1 to populate the dropdowns
          </span>
        ) : (
          <span style={{ marginLeft: 8 }}>· {sourceColumns.length} source columns detected</span>
        )}
      </div>
      <div
        style={{
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-md)",
          overflow: "hidden",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--color-bg-subtle)" }}>
              <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Canonical field</th>
              <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Source column</th>
            </tr>
          </thead>
          <tbody>
            {targets.map((target, i) => (
              <tr
                key={target}
                data-target-field={target}
                style={{
                  background: i % 2 === 0 ? "transparent" : "var(--color-surface-hover)",
                  borderTop: "1px solid var(--color-border-subtle)",
                }}
              >
                <td style={{ padding: "8px 12px", color: "var(--color-text)" }}>{target}</td>
                <td style={{ padding: "8px 12px" }}>
                  <select
                    value={mapping[target] ?? ""}
                    onChange={(e) => handleChange(target, e.target.value)}
                    style={{
                      width: "100%",
                      height: 30,
                      padding: "0 8px",
                      borderRadius: "var(--radius-sm)",
                      border: "1px solid var(--color-border)",
                      background: "var(--color-surface)",
                      fontSize: 13,
                      color: "var(--color-text)",
                    }}
                  >
                    <option value="">— not mapped —</option>
                    {sourceColumns.map((src) => (
                      <option key={src} value={src}>
                        {src}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
